import type { MouseEvent } from 'react';

import classNames from 'classnames';

import { IconFireFilled } from '@picsart/design-system/foundation/icons/IconFireFilled';
import { useTranslator } from '@picsart/rc/services/localization';

import { Button } from '@picsart/design-system/components/Button';
import {
  rememberStreakTrigger,
  useFreePassEnabled,
  useStreakState,
} from '@/features/free-pass';
import { useAppStore, setAppStore, type IAppStore } from '@/store';
import { Screen } from '@/types';
import sendAiGenScreenActionAnalytics from '@/utils/analytics/sendAiGenScreenActionAnalytics';
import getScreenType from '@/utils/analytics/getScreenType';
import { AiGenScreenAction } from '@/types/analytics';
import useStyles from './styles';

const selector = (store: IAppStore) => ({
  screen: store.screen,
  seeAllCategorySlug: store.seeAllCategorySlug,
});

const StreakPill = () => {
  const classes = useStyles();
  const translate = useTranslator();
  const enabled = useFreePassEnabled();
  const { screen, seeAllCategorySlug } = useAppStore(selector);
  const {
    streak: { canClaim, currentDayNumber, isFinalDay, isVisible },
    isGuest,
  } = useStreakState();

  const isOnLibraryScreen =
    screen === Screen.Library && seeAllCategorySlug === null;
  if (!enabled || !isOnLibraryScreen || !isVisible) return null;

  const handleClick = (event: MouseEvent<HTMLElement>) => {
    // From the event, not a ref: the DS Button forwards no ref. The sheet reads
    // this back on close — see `streakTriggerFocus`.
    rememberStreakTrigger(event.currentTarget);
    setAppStore({ isStreakSheetOpen: true });
    sendAiGenScreenActionAnalytics({
      action: AiGenScreenAction.STREAK_CLICK,
      screen_type: getScreenType(),
    });
    sendAiGenScreenActionAnalytics({
      action: isGuest
        ? AiGenScreenAction.STREAK_GUEST_SHEET_OPEN
        : AiGenScreenAction.STREAK_SHEET_OPEN,
      screen_type: getScreenType(),
    });
  };

  const daysLabel =
    currentDayNumber === 1
      ? `${currentDayNumber} Day`
      : `${currentDayNumber} Days`;
  return (
    <Button
      className={classNames(classes.pill, {
        [classes.pillGold]: isFinalDay,
      })}
      onClick={handleClick}
      aria-label={translate('Day Streak')}
      variant={canClaim || isFinalDay ? 'filled' : 'outlined'}
      dataTestId="streak-pill"
      startIcon={!canClaim ? IconFireFilled : undefined}
    >
      <span
        className={classNames(classes.claimText, {
          [classes.claimTextGold]: isFinalDay,
        })}
      >
        {translate(canClaim ? 'Claim' : daysLabel)}
      </span>
    </Button>
  );
};

export default StreakPill;
